import { Optional, Pipe, PipeTransform } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

@Pipe({
    name: 'dateAgo',
    // Impure so it re-evaluates on language change (Transloco reRenderOnLangChange),
    // mirroring MonthsToYearsAndMonthsPipe.
    pure: false,
    standalone: false
})
export class DateAgoPipe implements PipeTransform {

  // Largest-to-smallest so the first non-zero counter wins.
  private readonly _intervals: { unit: Intl.RelativeTimeFormatUnit; seconds: number }[] = [
    { unit: 'year', seconds: 31536000 },
    { unit: 'month', seconds: 2592000 },
    { unit: 'week', seconds: 604800 },
    { unit: 'day', seconds: 86400 },
    { unit: 'hour', seconds: 3600 },
    { unit: 'minute', seconds: 60 },
    { unit: 'second', seconds: 1 },
  ];

  // Cache the formatter per language so the impure pipe doesn't allocate a new
  // Intl.RelativeTimeFormat on every change-detection pass.
  private _cachedLang?: string;
  private _cachedFormat?: Intl.RelativeTimeFormat;

  constructor(@Optional() private readonly _translocoService: TranslocoService) {}

  transform(value: any): any {
    if (!value) {
      return value;
    }

    const seconds = Math.floor((+new Date() - +new Date(value)) / 1000);
    if (seconds < 29) { // less than 30 seconds ago shows as 'Just now'
      return this._translocoService?.translate('common.justNow') ?? 'Just now';
    }

    const lang = this._translocoService?.getActiveLang() ?? 'en';
    // Intl.RelativeTimeFormat handles pluralization and locale word order
    // (e.g. "2 days ago" / "hace 2 días") natively for the active language.
    const relativeTimeFormat = this.getRelativeTimeFormat(lang);

    for (const { unit, seconds: unitSeconds } of this._intervals) {
      const counter = Math.floor(seconds / unitSeconds);
      if (counter > 0) {
        return relativeTimeFormat.format(-counter, unit);
      }
    }

    return value;
  }

  private getRelativeTimeFormat(lang: string): Intl.RelativeTimeFormat {
    if (this._cachedLang !== lang || !this._cachedFormat) {
      this._cachedLang = lang;
      this._cachedFormat = new Intl.RelativeTimeFormat(lang, { numeric: 'always' });
    }
    return this._cachedFormat;
  }
}
